import React, {
    ReactElement,
    ReactNode,
    useEffect,
    useRef,
    useState,
} from "react";
import { useDomainContext } from "@hooks/useDomainContext";
import styles from "./style.module.scss";
import { useRouter } from "next/router";
import InsuranceOffer from "../insuranceOffer";
import { affiliateOfferZones } from "@lib/shared/config";
import Widget from "./widgets";
import OffersColumn from "./widgets/offersColumn";
import Offer from "@components/shared/offer";
import {
    getWidgetType,
    extractWidgets,
    removeParagraphById,
} from "@lib/thankyouUtils";
import MortgageOffer from "../mortgageOffer";
import { useBingEnhancedConversion } from "@hooks/useBingEnhancedConversion";
import { ListingV3 } from "src/api/listings";
import { fetchLeadBuyers } from "src/api/buyers";
import WinnerBuyerLoader from "./winnerBuyerLoader";
import {
    base64DecodeUnicode,
    SKIPPED_OPERATION_ID_KEY,
} from "@lib/sharedUtils";
import BuyerCallNotice from "./buyerCallNotice";
import { Buyer } from "@hooks/useForm/types";
import { handleFormAnswers } from "@lib/serverUtils";
import { DomainForm } from "src/api";
import AngiWidget, {
    AngiWidgetDetails,
    AngiWidgetState,
} from "./widgets/angiWidget/angiWidget";
import { createRoot } from "react-dom/client";
import SettlementCalculator from "./settlementCalculator";
interface FieldsState {
    [x: string]: string;
}
export const decodeFieldsFromText = (
    text: string,
    fieldsState: FieldsState | null,
) => {
    let clonedText = text;

    if (!fieldsState) return clonedText;

    const dynamicStrings = text?.match(/\${([^}]*)}/g);

    dynamicStrings?.forEach((item) => {
        if (fieldsState[item.replace("${", "").replace("}", "")]) {
            const temp = clonedText.replace(
                item,
                fieldsState[item.replace("${", "").replace("}", "")],
            );
            clonedText = temp;
        }
    });

    return clonedText;
};

export default function ThankYou({
    insuranceListingHeader,
    customThankYouMessage,
    hideListingTitle,
    inForm = false,
    isPageBuilder = false,
    className,
    form: formProp,
}: {
    insuranceListingHeader?: string;
    customThankYouMessage?: ReactNode;
    hideListingTitle?: boolean;
    inForm?: boolean;
    isPageBuilder?: boolean;
    className?: string;
    form?: DomainForm;
}): ReactElement {
    const { query } = useRouter();
    const { eb, data } = query;
    const urlData = data
        ? (JSON.parse(base64DecodeUnicode(data as string)) as Record<
              string,
              string
          >)
        : null;
    const {
        form: contextForm,
        clicksOffers,
        visitDetails,
        thankYouOffers,
        thankyouListing,
        secondListing,
        domain,
        category,
        setAngiWidgetDetails,
        angiWidgetDetails,
        locale,
        routeConfig,
    } = useDomainContext();

    const synchronizedForms =
        routeConfig?.templateConfig?.variantConfig?.synchronizedForms;

    // Prefer the form passed explicitly (e.g. a modal form when forms are
    // synchronized) over the domain-context form. When no prop is supplied
    // (standalone thank-you pages, non-synchronized flows) this falls back to
    // the context form, preserving the existing behavior exactly.
    const form = formProp && synchronizedForms ? formProp : contextForm;
    const [successMessageArray, setSuccessMessageArray] = useState<string[]>(
        [],
    );
    const [showCallNotice, setShowCallNotice] = useState(false);
    const [hideThankyouContent, setHideThankyouContent] = useState(true);
    const [loadingWinnerBuyer, setLoadingWinnerBuyer] = useState(false);
    const [showUnqualifiedOffers, setShowUnqualifiedOffers] = useState(false);
    const onlyShowWinnerBuyer = true;

    const [winnerBuyer, setWinnerBuyer] = useState<Buyer | null>(null);

    const [widgets, setWidgets] = useState<string[] | null>([]);

    const [angiWidgetState, setAngiWidgetState] =
        useState<AngiWidgetState>("loading");

    const hasInitialized = useRef(false);
    const calculatorMounted = useRef(false);

    const renderList = (list: ListingV3) => (
        <div className={`w-full pt-6 mt-6`} style={{ background: "#fbfbfb" }}>
            {list.pageTitle && !hideListingTitle && (
                <h2 className="text-xl md:text-2xl text-center md:text-left px-4 md:px-6 font-bold">
                    {list.pageTitle}
                </h2>
            )}
            {list?.offers?.map((offer, index) => (
                <Offer
                    zone={affiliateOfferZones.THANK_YOU}
                    offer={offer}
                    key={index}
                    className={`mt-0 ${styles["offersList"]}`}
                    style={{
                        borderStyle: "bottom",
                        background: "transparent",
                        borderColor: "#eee",
                        buttonBackground: "#3EA636",
                        buttonForground: "#fff",
                    }}
                />
            ))}
        </div>
    );

    useEffect(() => {
        if (hasInitialized.current) return;
        hasInitialized.current = true;

        const skippedOperationId = sessionStorage.getItem(
            SKIPPED_OPERATION_ID_KEY,
        );
        setHideThankyouContent(false);
        if (skippedOperationId) {
            setLoadingWinnerBuyer(true);
            const start = Date.now();
            fetchLeadBuyers(skippedOperationId)
                .then((res) => {
                    if (
                        res.data?.status === "SUCCESS" &&
                        res.data?.buyers?.length > 0
                    ) {
                        const angiWidgetConfig = res.data.buyers[0].integration
                            ?.response as AngiWidgetDetails;

                        if (res.data.buyers[0].callOnThankYou) {
                            setShowCallNotice(true);
                        }
                        setWinnerBuyer(res.data.buyers[0]);

                        let widgetDetails: AngiWidgetDetails;

                        if (
                            angiWidgetConfig?.cjLeadKey === "" ||
                            angiWidgetConfig?.showWidget === false
                        ) {
                            widgetDetails = {
                                showWidget: false,
                                cjLeadKey: angiWidgetConfig.cjLeadKey ?? "",
                            };
                        } else {
                            widgetDetails = {
                                showWidget: angiWidgetConfig.showWidget,
                                cjLeadKey: angiWidgetConfig.cjLeadKey ?? "",
                            };
                        }
                        setAngiWidgetDetails(widgetDetails);
                        // If Angi widget will show, skip the winner buyer loader
                        // and go straight to Angi widget skeleton to avoid blank white screen
                        if (widgetDetails.showWidget) {
                            setLoadingWinnerBuyer(false);
                        }
                    } else {
                        if (res.data?.showUnqualifiedOffers) {
                            setShowUnqualifiedOffers(true);
                        }
                        setAngiWidgetDetails({
                            showWidget: false,
                            cjLeadKey: "",
                        });
                    }
                })
                .catch(() => {
                    setAngiWidgetDetails({
                        showWidget: false,
                        cjLeadKey: "",
                    });
                })
                .finally(() => {
                    const elapsed = Date.now() - start;
                    const minDuration =
                        category?.config?.winnerBuyerLoadingMinDuration ?? 3000;
                    if (elapsed < minDuration) {
                        setTimeout(
                            () => setLoadingWinnerBuyer(false),
                            minDuration - elapsed,
                        );
                    } else {
                        setLoadingWinnerBuyer(false);
                    }
                });
        } else {
            setAngiWidgetDetails({
                showWidget: false,
                cjLeadKey: "",
            });
        }

        const customDomainThankYouMessage = domain?.textBlocks?.find(
            (textBlock) => textBlock.position === "custom-domain-thank-you",
        )?.text;

        const fieldsData = JSON.parse(
            localStorage.getItem("dynamicFields") as string,
        ) as FieldsState | null;
        const finalFormAnswers = fieldsData || urlData;
        handleFormAnswers(visitDetails);

        const [widgets, successMessageArrayAfterSplitting] = extractWidgets(
            customDomainThankYouMessage
                ? decodeFieldsFromText(
                      customDomainThankYouMessage,
                      finalFormAnswers,
                  )
                : form
                ? decodeFieldsFromText(form.successMessage, finalFormAnswers)
                : "",
        );
        setWidgets(widgets ? widgets : null);
        setSuccessMessageArray(successMessageArrayAfterSplitting as string[]);
    }, []);

    // Mount SettlementCalculator when DOM content is ready
    const mountCalculator = (node: HTMLDivElement | null) => {
        if (!node || calculatorMounted.current) return;

        const anchor = node.querySelector("#settlement-calculator-anchor");
        if (anchor && !anchor.hasAttribute("data-root-mounted")) {
            calculatorMounted.current = true;
            const root = createRoot(anchor);
            root.render(<SettlementCalculator locale={locale} />);
            anchor.setAttribute("data-root-mounted", "true");
        }
    };

    useBingEnhancedConversion();

    const isAngiWidgetActive = angiWidgetDetails?.showWidget === true;
    const showThankYouContent =
        !isAngiWidgetActive || angiWidgetState === "thankYou";

    if (form || customThankYouMessage) {
        return (
            <>
                <div className={`${styles["thank-you-page"]} pb-10 relative`}>
                    {/* Angi widget - render once and keep mounted */}
                    {isAngiWidgetActive && !inForm && (
                        <AngiWidget
                            angiWidgetDetails={angiWidgetDetails}
                            onStateChange={setAngiWidgetState}
                        />
                    )}

                    {!hideThankyouContent && (showThankYouContent || inForm) && (
                        <>
                            {showCallNotice &&
                                winnerBuyer &&
                                !loadingWinnerBuyer && (
                                    <BuyerCallNotice
                                        winnerBuyer={winnerBuyer}
                                    />
                                )}
                            <div>
                                {customThankYouMessage ? (
                                    <div
                                        id="thank-you-page"
                                        className={className}
                                    >
                                        {customThankYouMessage}
                                    </div>
                                ) : (
                                    <div
                                        className={
                                            thankYouOffers &&
                                            thankYouOffers.length &&
                                            (!widgets ||
                                                widgets.length === 0) &&
                                            !inForm
                                                ? "flex justify-center items-start flex-col-reverse md:flex-row"
                                                : ""
                                        }
                                    >
                                        <div
                                            id="thank-you-page"
                                            className={
                                                (thankYouOffers &&
                                                thankYouOffers.length &&
                                                (!widgets ||
                                                    widgets.length === 0) &&
                                                !inForm
                                                    ? "w-full md:w-2/4 lg:w-2/3"
                                                    : "") +
                                                ` ${className ?? ""}`
                                            }
                                        >
                                            {loadingWinnerBuyer ? (
                                                <WinnerBuyerLoader className="fixed inset-0 z-50 flex items-center justify-center bg-white" />
                                            ) : (
                                                successMessageArray.map(
                                                    (content, index) => {
                                                        const finalContent =
                                                            removeParagraphById(
                                                                content,
                                                                eb,
                                                            );
                                                        return (
                                                            <div key={index}>
                                                                <div
                                                                    ref={
                                                                        mountCalculator
                                                                    }
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: finalContent,
                                                                    }}
                                                                ></div>
                                                                {widgets?.[
                                                                    index
                                                                ] ? (
                                                                    <Widget
                                                                        type={getWidgetType(
                                                                            widgets[
                                                                                index
                                                                            ],
                                                                        )}
                                                                        winnerBuyer={
                                                                            winnerBuyer ??
                                                                            null
                                                                        }
                                                                        loadingWinnerBuyer={
                                                                            loadingWinnerBuyer
                                                                        }
                                                                        onlyShowWinnerBuyer={
                                                                            onlyShowWinnerBuyer
                                                                        }
                                                                    />
                                                                ) : (
                                                                    <></>
                                                                )}
                                                            </div>
                                                        );
                                                    },
                                                )
                                            )}
                                        </div>
                                        {!inForm &&
                                        !loadingWinnerBuyer &&
                                        (!widgets || widgets.length === 0) ? (
                                            <OffersColumn
                                                winnerBuyer={
                                                    winnerBuyer ?? null
                                                }
                                                loadingWinnerBuyer={
                                                    loadingWinnerBuyer
                                                }
                                                onlyShowWinnerBuyer={
                                                    onlyShowWinnerBuyer
                                                }
                                                showUnqualifiedOffers={
                                                    showUnqualifiedOffers
                                                }
                                                template="thankYou"
                                            />
                                        ) : (
                                            <></>
                                        )}
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {!inForm &&
                    !isPageBuilder &&
                    insuranceListingHeader &&
                    clicksOffers &&
                    clicksOffers.length &&
                    !clicksOffers[0].isMortgage ? (
                        <h2 className="my-4 pt-10 font-bold text-center text-lg md:text-xl">
                            {insuranceListingHeader}
                        </h2>
                    ) : (
                        <></>
                    )}

                    {clicksOffers && !inForm && !isPageBuilder && (
                        <div className="mt-10 md:mt-5 w-full">
                            {clicksOffers
                                // ?.slice(0, 2)
                                ?.map((clickOffer, index) =>
                                    !clickOffer.isMortgage ? (
                                        <InsuranceOffer
                                            key={index}
                                            index={index}
                                            item={clickOffer}
                                            category="Auto Insurance"
                                            state={visitDetails?.region}
                                            primaryColor="#f79626"
                                            textColor="#266199"
                                            btnLabel="I Want That"
                                        />
                                    ) : (
                                        <MortgageOffer
                                            key={index}
                                            // index={index}
                                            offer={clickOffer}
                                        />
                                    ),
                                )}
                        </div>
                    )}

                    {secondListing &&
                    secondListing?.offers?.length &&
                    !inForm ? (
                        renderList(secondListing)
                    ) : (
                        <></>
                    )}
                    {thankyouListing &&
                    thankyouListing?.offers?.length &&
                    !inForm ? (
                        renderList(thankyouListing)
                    ) : (
                        <></>
                    )}
                </div>
            </>
        );
    }

    return <></>;
}
